import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import validators from '@/helpers/validators';
import ApiErrorCode from '@/models/enums/ApiErrorCode';
import { FormValidationError } from '@/models/errors';
import JwtAuthToken from '@/models/JwtAuthToken';
import ApiError from '@/models/resources/ApiError';
import OfertaSugeridaResource from '@/models/resources/OfertaSugeridaResource';
import { authService, queryStringService, sessionService, storageService } from '@/services';

/**
 * ## Fluxo de Rotacionamento de Contato
 *
 * Indica qual canal de contato foi rotacionado e qual campo deve ser solicitado no cadastro:
 * - `'e'` — e-mail foi rotacionado → solicita **telefone**
 * - `'t'` — telefone foi rotacionado → solicita **e-mail**
 *
 * ### Fontes (em ordem de prioridade):
 * 1. **Query param `typeId`** da URL (capturado em `obterDadosDaUrl`)
 * 2. **Bearer Token da sessão** (claim `tipoContatoRotacionado`, após login)
 */
export type TipoContatoRotacionado = 'e' | 't';

function parseTipoDoJwt(jwtString: string): TipoContatoRotacionado | undefined {
  try {
    const payload = jwtDecode<{ tipoContatoRotacionado?: unknown }>(jwtString);
    const v = payload.tipoContatoRotacionado;
    if (v === 'e' || v === 't') {
      return v;
    }
  } catch {
    /* token inválido ou não-JWT */
  }
  return undefined;
}

function validarDadosCadastro(
  dados: {
    dataNascimento: string;
    telefoneCelular?: string;
    email?: string;
    termosCondicoes: boolean;
  },
  tipoContato: string | undefined
) {
  const errors = new FormValidationError();

  if (!dados.termosCondicoes) {
    errors.add('termosCondicoes', 'Você precisa aceitar os termos e condições.');
  }

  if (!validators.birthdate(dados.dataNascimento)) {
    errors.add('dataNascimento', 'A data de nascimento não é válida.');
  }

  if (tipoContato === 't') {
    const email = dados.email?.trim() ?? '';
    if (!validators.email(email)) {
      errors.add('email', 'O e-mail não é válido.');
    }
  } else {
    const telefone = dados.telefoneCelular ?? '';
    if (!validators.phone(telefone)) {
      errors.add('telefoneCelular', 'O telefone celular não é válido.');
    }
  }

  return errors;
}

function stripRedirectFromPersistedQuery(): void {
  const q = queryStringService.getFromStorage();
  if (!q.redirect) {
    return;
  }
  const next = { ...q };
  delete next.redirect;
  const mergedQueryAsString = queryStringService.getAsString(next);
  storageService.queryString.set(mergedQueryAsString ? `?${mergedQueryAsString}` : '');
}

function lerCaptacaoPersistida() {
  return storageService?.deeplinkCaptacao?.get() ?? null;
}

const deeplink = defineStore('deeplink', () => {
  const captacaoPersistida = lerCaptacaoPersistida();
  const token = ref<string | undefined>(captacaoPersistida?.token ?? '');
  const api = ref<string>();
  const dividaId = ref<string>();
  const ofertaId = ref<string>();
  const dataValidade = ref<string>();
  const primeiroNome = ref<string>();
  const typeId = ref<string | undefined>(captacaoPersistida?.typeId);
  const id = ref<string | undefined>(captacaoPersistida?.id);
  const primeiraTentativaDeCadastroRealizada = ref<boolean>(
    captacaoPersistida?.primeiraTentativaDeCadastroRealizada ?? false
  );

  function persistirCaptacao(): void {
    const entry = storageService?.deeplinkCaptacao;
    if (!entry) return;

    const temContexto =
      Boolean(token.value) || Boolean(typeId.value) || Boolean(id.value) || primeiraTentativaDeCadastroRealizada.value;

    if (!temContexto) {
      entry.remove();
      return;
    }

    entry.set({
      token: token.value || undefined,
      typeId: typeId.value,
      id: id.value,
      primeiraTentativaDeCadastroRealizada: primeiraTentativaDeCadastroRealizada.value,
    });
  }

  // region deeplink
  const credorPrioritarioDeeplink = ref<string>('');
  const dividaIdPrioritarioDeeplink = ref<number>(0);
  const deeplinkPayload = ref<string | null>(null);

  /**
   * Quando true, a navegação pós-deeplink deve prevalecer sobre redirect propagado.
   * Consumido ao chegar em `/dividas` (listagem de dívidas).
   */
  const isDeepLinkJourneyActive = computed(() => dividaIdPrioritarioDeeplink.value > 0);

  /**
   * `jti` do login feito por deeplink (oferta, área logada ou captação). Ao contrário de
   * `isDeepLinkJourneyActive`, vale também para o link sem dívida no payload, e só para esse login.
   */
  const jtiLoginDeeplink = ref<string | null>(null);

  function isLoginPorDeeplink(): boolean {
    const jti = sessionService.getTokenMetadata()?.jti;
    return Boolean(jti) && jti === jtiLoginDeeplink.value;
  }

  // region ofertaDeeplink
  const credorOfertaDeeplink = ref<string>('');
  const dividaIdOfertaDeeplink = ref<number>(0);
  const ofertaSugerida = ref<OfertaSugeridaResource | null>(null);
  const logoCredor = computed(() => ofertaSugerida.value?.logo ?? credorOfertaDeeplink.value);

  const tipoRotacionamento = computed(() => {
    if (typeId.value != null && typeId.value !== '') {
      return typeId.value;
    }
    const sessionJwt = sessionService.token?.rawToken;
    if (sessionJwt) {
      return parseTipoDoJwt(sessionJwt);
    }
    return undefined;
  });
  // endregion

  /**
   * Limpa priorização/oferta de um deeplink anterior na mesma SPA
   * (evita vazamento para DividasSyncService).
   */
  function resetStaleDeeplinkContext(): void {
    credorPrioritarioDeeplink.value = '';
    dividaIdPrioritarioDeeplink.value = 0;
    deeplinkPayload.value = null;
    credorOfertaDeeplink.value = '';
    dividaIdOfertaDeeplink.value = 0;
    ofertaSugerida.value = null;
  }

  /**
   * Remove redirect persistido antes de iniciar navegação pós-deeplink. Chamado logo após o login
   * por deeplink, com a sessão já gravada.
   */
  function beginDeepLinkJourney(): void {
    stripRedirectFromPersistedQuery();
    jtiLoginDeeplink.value = sessionService.getTokenMetadata()?.jti ?? null;
  }

  function setDeeplink(params: { credorApi: string; dividaId?: number; payload?: string }) {
    credorPrioritarioDeeplink.value = params.credorApi;
    if (typeof params.dividaId === 'number' && Number.isFinite(params.dividaId)) {
      dividaIdPrioritarioDeeplink.value = params.dividaId;
    }
    deeplinkPayload.value = params.payload ?? null;
  }
  // endregion

  function obterDadosDaUrl() {
    const urlSearchParams = new URLSearchParams(window.location.search);

    const queryToken = urlSearchParams.get('token') ?? undefined;
    const payload = urlSearchParams.get('payload') ?? undefined;
    if (payload) {
      [api.value, dividaId.value] = atob(payload).split('|');
    }

    if (queryToken) {
      token.value = encodeURIComponent(queryToken);
    } else {
      token.value = undefined;
    }
    dataValidade.value = urlSearchParams.get('dataValidade') ?? undefined;

    const rawTypeId = urlSearchParams.get('typeId');
    typeId.value = rawTypeId && rawTypeId !== '' ? rawTypeId : undefined;

    const rawId = urlSearchParams.get('id');
    id.value = rawId && rawId !== '' ? rawId : undefined;

    urlSearchParams.delete('token');
    urlSearchParams.delete('payload');
    urlSearchParams.delete('dataValidade');
    urlSearchParams.delete('typeId');
    urlSearchParams.delete('id');

    storageService.queryString.set(urlSearchParams.toString());
    persistirCaptacao();
  }

  async function cadastrar(dados: {
    dataNascimento: string;
    telefoneCelular?: string;
    email?: string;
    termosCondicoes: boolean;
  }) {
    if (!token.value) throw new Error('Token não encontrado.');

    const tipo = tipoRotacionamento.value;
    const errors = validarDadosCadastro(dados, tipo);

    if (errors.length) {
      throw errors;
    }

    const typeIdParaApi = typeId.value ?? tipo;

    try {
      const base = {
        token: token.value,
        dataNascimento: dayjs(dados.dataNascimento, 'DD/MM/YYYY').toDate(),
        ...(typeIdParaApi ? { typeId: typeIdParaApi } : {}),
        ...(id.value ? { id: id.value } : {}),
      };

      const requestPayload =
        tipo === 't'
          ? { ...base, email: dados.email?.trim() }
          : { ...base, telefoneCelular: dados.telefoneCelular ?? '' };

      const { accessToken } = await authService.cadastrarComDeeplink(requestPayload);

      const jwtAuthToken = new JwtAuthToken(accessToken);

      if (tipoRotacionamento.value === 't') {
        primeiraTentativaDeCadastroRealizada.value = true;
      }

      sessionService.login(jwtAuthToken);
      persistirCaptacao();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.is(ApiErrorCode.DEEPLINK_CAPTACAO_EMAIL_JA_CADASTRADO)) {
          throw error;
        }

        const formValidationError = new FormValidationError();

        if (error.is(ApiErrorCode.CADASTRO_DATA_DE_NASCIMENTO_INVALIDA)) {
          formValidationError.add('dataNascimento', 'A data de nascimento não é válida.');
        }

        if (error.is(ApiErrorCode.CADASTRO_TELEFONE_INVALIDO)) {
          formValidationError.add('telefoneCelular', 'O telefone celular não é válido.');
        }

        throw formValidationError.length > 0 ? formValidationError : error;
      }

      throw error;
    }
  }

  return {
    isDeepLinkJourneyActive,
    isLoginPorDeeplink,
    resetStaleDeeplinkContext,
    beginDeepLinkJourney,
    token,
    api,
    dividaId,
    ofertaId,
    dataValidade,
    primeiroNome,
    typeId,
    id,
    obterDadosDaUrl,
    cadastrar,
    credorPrioritarioDeeplink,
    dividaIdPrioritarioDeeplink,
    deeplinkPayload,
    primeiraTentativaDeCadastroRealizada,
    setDeeplink,
    credorOfertaDeeplink,
    dividaIdOfertaDeeplink,
    ofertaSugerida,
    logoCredor,
    tipoRotacionamento,
  };
});

export default deeplink;
